<template>
  <div id="wrapper">
    <!--
    Valentijns hartjes
    <div class="hearts">
      <div class="heart"></div>
      <div class="heart"></div>
      <div class="heart"></div>
      <div class="heart"></div>
    </div> -->

    <router-view v-if="skipDataLoad" />
    <template v-else>
      <div v-if="!loaded" class="x-reset-container">
        <!-- <div class="x-reset-container"> -->
        <div class="im">
          <img :src="loaderLogo" style="height: 180px;" />

          <div class="im__fill"></div>
        </div>
        <div class="errors">
          <div v-for="(item, index) in errors" :key="index">{{ item }}</div>
        </div>
        <div class="tasks">
          <div v-for="(item, index) in tasks" :key="index">{{ item }}</div>
        </div>
      </div>
      <router-view v-if="loaded" />
      <DateBar v-if="loaded && showDateBar" />
    </template>
  </div>
</template>

<script>
import { useStore } from 'vuex'
import { useRouter, useRoute } from 'vue-router'

import { nextTick, onMounted, computed, ref, onUnmounted, watch } from 'vue'
import shared from './shared/shared.js'
import { Vertical } from './enums/vertical-enum.js';
import { useFetch } from './composables/useFetch';
import DateBar from './components/DateBar.vue';
import {
  defaultWallboardDate,
  isHistoricalWallboardDate,
  isLiveWallboard,
} from './shared/wallboardDate';
import { isFlexgateEnabled } from './shared/flexgate';
import { EventSourcePolyfill } from 'event-source-polyfill';
import { getDevToken } from './shared/devToken';
import { isJwt } from './shared/ssoAuth';

const LEGACY_OAUTH_PARAMS = ['code', 'scope', 'state', 'session_state'];

/** Path for router.replace. An absolute URL is a relative location in Vue Router. */
function legacyOauthRedirectPath(href) {
  const url = new URL(href, window.location.origin);
  let changed = false;
  for (const key of LEGACY_OAUTH_PARAMS) {
    if (!url.searchParams.has(key)) continue;
    url.searchParams.delete(key);
    changed = true;
  }
  if (!changed) return '';
  return `${url.pathname}${url.search}${url.hash}`;
}

export default {
  components: { DateBar },
  setup() {

    const { getSSEUrl } = shared()
    let event_source = null
    let clock_timer = false
    let errors = ref([])
    let tasks = ref([])

    // errors.value.push('test')

    const router = useRouter()
    const route = useRoute()

    const store = useStore();
    const loaded = computed(() => store.state.loaded);
    const skipDataLoad = computed(() => !!route.meta.skipDataLoad);
    const showDateBar = !isLiveWallboard();

    /**
     * Soft-fail fetch: log errors but always resolve with fallback so the
     * wallboard can still render when optional endpoints (e.g. compliance) are down.
     */
    async function fetchJson(url, name, fallback, params = {}) {
      if (!url) {
        getErr('missing url', name);
        return fallback;
      }
      try {
        const response = await useFetch(url, params);
        const data = await response.json().catch(() => null);
        if (!response.ok) {
          const msg =
            (data && (data.detail || data.error?.message || data.message)) ||
            `${response.status} ${response.statusText}`;
          getErr(msg, name);
          return fallback;
        }
        if (data && data.detail) {
          getErr(data.detail, name);
          return fallback;
        }
        if (data && data.error) {
          getErr(data.error.message || data.error, name);
          return fallback;
        }
        return data;
      } catch (err) {
        getErr(err, name);
        return fallback;
      }
    }

    async function loadPartOne() {
      const fetched = await Promise.all([
        fetchJson(process.env.VUE_APP_SUMMARY_PARTNER, 'sumpart', [], { addQueryParams: true }),
        fetchJson(process.env.VUE_APP_SUMMARY_TEAMS, 'sumteams', [], { addQueryParams: true }),
        fetchJson(process.env.VUE_APP_DETAIL, 'detail', [], { addQueryParams: true }),
        fetchJson(process.env.VUE_APP_COMPLIANCE, 'compl', []),
        fetchJson(process.env.VUE_APP_TARGETS, 'targ', [], { addQueryParams: true }),
        fetchJson(process.env.VUE_APP_CONTACT_ENDPOINT, 'cont', []),
        fetchJson(process.env.VUE_APP_SALES_TOTAL, 'revsum', {}, { addQueryParams: true }),
        isFlexgateEnabled()
          ? fetchJson(process.env.VUE_APP_AWARDS, 'aw', { data: [] })
          : Promise.resolve({ data: [] }),
        isFlexgateEnabled()
          ? fetchJson(process.env.VUE_APP_PARTNER_LEAGUES, 'leagues', { data: [] })
          : Promise.resolve({ data: [] }),
      ]);
      const dataSummaryPartner = fetched[0];
      const dataSummaryTeams = fetched[1];
      const dataDetailPartner = fetched[2];
      const compliance = fetched[3];
      const targets = fetched[4];
      const dataContacts = fetched[5];
      const revenueSummary = fetched[6];
      const awardsPayload = fetched[7];
      const leaguesPayload = fetched[8];

      store.dispatch('setSummaryPartners', dataSummaryPartner || []);
      tasks.value.push(1);
      store.dispatch('setSummaryTeams', dataSummaryTeams || []);
      tasks.value.push(2);
      store.dispatch('setDetails2', dataDetailPartner || []);
      tasks.value.push(2);
      store.dispatch('setCompliance', Array.isArray(compliance) ? compliance : []);
      tasks.value.push(3);
      store.dispatch('setTargets', targets || []);
      tasks.value.push(4);
      store.dispatch('setContacts', Array.isArray(dataContacts) ? dataContacts : []);
      tasks.value.push(5);
      store.commit('setRevenueSummary', revenueSummary || {});
      tasks.value.push(6);
      store.commit('setAwards', (awardsPayload && awardsPayload.data) || []);
      store.commit('setLeagues', (leaguesPayload && leaguesPayload.data) || []);
      await loadPartTwo();
    }

    function getErr(err, name) {
      errors.value.push(`${name} ${err}`)
    }

    async function loadPartTwo() {
      const fetched = await Promise.all([
        fetchJson(process.env.VUE_APP_FEED_ENDPOINT, 'feeds', { items: [] }),
        fetchJson(process.env.VUE_APP_ADVERTISERS, 'adv', []),
        fetchJson(process.env.VUE_APP_REVIEWS, 'reviews', []),
        fetchJson(process.env.VUE_APP_CHARTS, 'charts', [], { addQueryParams: true }),
      ]);
      const dataFeeds = fetched[0];
      const dataAdvertisers = fetched[1];
      const dataReviews = fetched[2];
      const dataCharts = fetched[3];

      store.dispatch(
        'setFeeds',
        dataFeeds && Array.isArray(dataFeeds.items)
          ? dataFeeds
          : { items: [] }
      );
      tasks.value.push(7);
      store.dispatch('setAdvertisers', dataAdvertisers || []);
      tasks.value.push(8);
      store.dispatch(
        'setReviews',
        Array.isArray(dataReviews) ? dataReviews : dataReviews?.data || []
      );
      tasks.value.push(10);
      store.dispatch('setCharts', dataCharts || []);
      tasks.value.push(11);
      setTimeout(() => {
        store.dispatch('setLoaded', true);
      }, 1000);
    }

    function setCurrentTime(currentTime) {
      // console.log(currentTime)
      store.dispatch('setCurrentTime', currentTime);
    }

    function startClock() {
      if (clock_timer === false) {
        clock_timer = setInterval(function () {
          const d = new Date();
          const hh = String(d.getHours()).padStart(2, '0');
          const mm = String(d.getMinutes()).padStart(2, '0');
          setCurrentTime(`${hh}:${mm}`);
        }, 1000);
      }
    }

    const loaderLogo = computed(function () {
      const theme = store.state.theme;
      if (theme == 'improvers') {
        return 'https://improvers-prod-images-bucket.s3.eu-central-1.amazonaws.com/logo/rgb_im_sapphire.svg';
      } else {
        return 'https://improvers-prod-images-bucket.s3.eu-central-1.amazonaws.com/logo/rgb_' + theme + '_beeldmerk_square_darkpurple.svg';
      }
    });

    onUnmounted(() => {
      clock_timer = null;
    })



    async function ensureDefaultDate() {
      if (route.query.date) return;
      await router.replace({
        query: { ...route.query, date: defaultWallboardDate() },
      });
    }

    async function reloadWallboardData() {
      errors.value = [];
      tasks.value = [];
      store.dispatch('setLoaded', false);
      if (event_source) {
        event_source.close();
        event_source = null;
      }
      await loadPartOne();
      if (showDateBar && isHistoricalWallboardDate(route.query.date)) {
        return;
      }
      setupStream();
    }

    watch(
      () => route.query.date,
      async (next, prev) => {
        if (route.meta.skipDataLoad) return;
        if (!next || next === prev) return;
        if (prev === undefined) return;
        await reloadWallboardData();
      }
    );

    onMounted(async function () {
      /// set timeout
      const random = Math.random()
      console.log('random', random)

      const theme = new URL(window.location.href).searchParams.get("theme");
      if (!theme || theme === 'improvers') {
        store.dispatch('setTheme', 'improvers')

      } else {
        store.dispatch('setTheme', 'cherries')
      }

      // Mount happens before the first navigation finishes, so route meta is
      // still empty. Without this, /auth/sso is treated as a wallboard page.
      await router.isReady();

      if (route.meta.skipDataLoad) {
        return;
      }

      setTimeout(async () => {
        if (showDateBar) {
          await ensureDefaultDate();
        }
        await loadPartOne();
        if (!(showDateBar && isHistoricalWallboardDate(route.query.date))) {
          setupStream();
        }
        startClock();

        const nextPath = legacyOauthRedirectPath(window.location.href);
        if (nextPath) {
          await router.replace(nextPath);
        }
      }, 1);
      //}, Math.floor(random * 5000));



    })

    function sseHeaders() {
      const token =
        store.getters.getAccessToken ||
        getDevToken(route.query.token) ||
        null;
      if (!token) {
        return {};
      }
      if (isJwt(token)) {
        return { Authorization: "Bearer " + token };
      }
      return { "x-api-key": token };
    }

    function setupStream() {
      event_source = handle_event_source(
        getSSEUrl(process.env.VUE_APP_SSE_ENDPOINT)
      );

      function handle_event_source(url) {
        event_source = new EventSourcePolyfill(url, { headers: sseHeaders() });
        event_source.addEventListener('message',
          (event) => {
            const data = JSON.parse(event.data);
            if (data.message_type === 'sale') {
              // store.dispatch('upsertCurrentSale', data);
              faster_process(data);

            } else {
              store.distpatch('upsertContact', data);
            }
          }, false);
        event_source.addEventListener('heartbeat',
          (event) => {
            if (store.getters.getDate) {
              if (!event.data.includes(store.getters.getDate)) {
                window.location.reload();
              }
            }
          }, false);
      }

      function is_vertical_match(data) {
        const vertical = Vertical[data.vertical];
        const query = route.query
        if (!query.vertical) return true;

        if (query.vertical !== vertical) {
          console.log('Skipping add sale, vertical mismatch', vertical, query.vertical)
          return false;
        }
        return true;
      }

      function is_campaign_match(data) {
        const query = route.query
        if (!query.campaignCodes) return true;

        const partialCampaignCode = data.campaign_code.substring(0, 4)
        const campaignCodes = query.campaignCodes.split(',')

        if (!campaignCodes.includes(partialCampaignCode) && !campaignCodes.includes(data.campaign_code)) {
          console.log('Skipping add sale, campaign code mismatch', data.campaign_code, campaignCodes)
          return false;
        }

        return true;
      }

      function faster_process(data) {
        if (!is_vertical_match(data)) return;
        if (!is_campaign_match(data)) return;
        store.dispatch('upsertCurrentSaleTemp', data);

        if (store.state.inloop == 0) {
          store.state.inloop = 1
          temp_loop()
        }

      }

      function temp_loop() {
        if (store.state.inloop == 1) {
          console.log('size before', store.state.temp_sales.length)
          const sale = store.state.temp_sales.shift()
          store.dispatch('upsertCurrentSale', sale);
          nextTick(() => {
            console.log('next tick')
            if (store.state.temp_sales.length == 0) {
              store.state.inloop = 0
              console.log('loop stopped')
            } else {
              console.log('still looping')
              temp_loop()
            }
          })
        }
      }
    }
    return { loaded, skipDataLoad, showDateBar, errors, tasks, loaderLogo }
  },
}

</script>


<style>
:root {
  --color-heart: red;
  --size-viewport: 85%;
}

.hearts {
  bottom: 0%;
  left: 70%;
}

.hearts {
  width: 5%;
  min-width: 100px;
  height: 25%;
  min-height: 100%;
  position: absolute;
  z-index: 10;
}

.heart {
  background-color: var(--color-heart);
  position: absolute;
  height: 20%;
  min-height: 15px;
  width: 20%;
  min-width: 15px;
  opacity: 0;
  -webkit-mask-image: url('~@/assets/images/heart_mask.svg');
  mask-image: url('~@/assets/images/heart_mask.svg');
  /* -webkit-mask-repeat: no-repeat; */
  mask-repeat: no-repeat;
  /* -webkit-mask-position: bottom; */
  mask-position: bottom;
  animation: floating-heart 10s infinite cubic-bezier(0.5, 0.5, 0.5, 0.5);
}

.heart:nth-child(1) {
  animation-delay: 1s;
}

.heart:nth-child(2) {
  animation-delay: 2.5s;
}

.heart:nth-child(3) {
  animation-delay: 3.5s;
}

.heart:nth-child(4) {
  animation-delay: 4.5s;
}

@keyframes floating-heart {
  0% {
    opacity: 0;
    bottom: 0%;
    left: 0%;
  }

  10% {
    opacity: 1;
    bottom: 20%;
    left: 70%;
  }

  20% {
    bottom: 40%;
    left: 10%;
  }

  30% {
    bottom: 60%;
    left: 50%;
  }

  40% {
    opacity: 1;
    bottom: 80%;
    left: 5%;
  }

  48% {
    opacity: 0;
    bottom: 100%;
    left: 60%;
  }

  100% {
    opacity: 0;
    bottom: 100%;
    left: 90%;
  }
}
</style>

